import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { StorageUtils } from '../../../utils/storage-utils';
import { Cnae, Company } from './company';
import { CompanySearch } from './company-search';
import { RestService } from '../../../utils/rest-service';
import { JsonContainer } from '../../../utils/json-container';
import { FileUpload } from '../../ok-core/photo/file-upload';
import { Lc116 } from "../lc116/lc116";
import { TaxCode } from "../tax-code/tax-code";
import { CompanyProcessSearch } from './company-process-search';
import { Particularity } from 'app/pages/particularity/particularity/particularity';

export interface PlanLinkResponse {
    link: string;
}


@Injectable()
export class CompanyService extends RestService {

    constructor(private client: HttpClient, private storageUtils: StorageUtils) {
        super(client, 'companyExt', storageUtils);
    }

    save(company: Company, callback: (result: JsonContainer, company: Company) => void) {
        this.executePost('save', company, (result: JsonContainer) => {
            callback(result, company);
        });
    }
    
    searchCompanyProcessByIdPartner(search: CompanyProcessSearch, callback: (result: JsonContainer, list: any) => void) {
        this.executePut('/getCompaniesPartner/', search, callback)
    }

    saveAvatar(fileUpload: FileUpload, callback: (result: JsonContainer, list: any) => void) {
        this.executePost('saveAvatar', fileUpload, callback)
    }
    
    searchCertificades(companySearch: CompanySearch, callback: (result: JsonContainer, companyCards: Company[]) => void) {
        this.executePost('searchCertificades', companySearch, callback)
    }

    nextCertificadesDates( callback: (result: JsonContainer, list: any[]) => void) {
        this.executeGet('nextCertificadesDates', callback)
    }

    searchAdmin(companySearch: CompanySearch, callback: (result: JsonContainer, companyCards: Company[]) => void) {
        this.executePost('searchResponse', companySearch, callback)
    }

    findByIdParent(idParent: string, callback: (result: JsonContainer, quantity: Number) => void) {
        this.executeGet(`findByIdParent/${idParent}`, callback)
    }

    saveMobile(company: Company, callback: (result: JsonContainer, company: Company) => void) {
        this.executePost('saveMobile', company, callback)
    }

    changeStatus(company: Company, callback: (result: JsonContainer, company: Company) => void) {
        this.executePut('changeStatus', company, callback)
    }

    listAll(callback: (result: JsonContainer, roles: Company[]) => void) {
        this.executeGet('listAll', callback)
    }

    listPendingApprove(callback: (result: JsonContainer, roles: Company[]) => void) {
        this.executeGet('listPendingApprove', callback)
    }

    listActiveLPSNCustomers(callback: (result: JsonContainer, roles: Company[]) => void) {
        this.executeGet('listActiveLPSNCustomers', callback)
    }

    listActives(callback: (result: JsonContainer, roles: Company[]) => void) {
        this.executeGet('listActives', callback)
    }

    listActiveCards(callback: (result: JsonContainer, roles: Company[]) => void) {
        this.executeGet('listActiveCards', callback)
    }

    returnAddress(cep: string, callback: (result: JsonContainer, roles: Company[]) => void) {
        this.executeGetUrl('https://viacep.com.br/ws/' + cep + '/json', callback)
    }

    search(companySearch: CompanySearch, callback: (result: JsonContainer, companyCards: Company[]) => void) {
        this.executePost('search', companySearch, callback)
    }

    searchAdminExt(companySearch: CompanySearch, callback: (result: JsonContainer, companyCards: Company[]) => void) {
        this.executePost('searchAdminExt', companySearch, callback)
    }

    searchPartner(companySearch: CompanySearch) {
        return this.executePostCustom('search', companySearch)
    }
    loadByDocument(document: string, callback: (result: JsonContainer, company: Company) => void) {
        this.executeGet('loadByDocument/' + document, callback)
    }

    deleteCertified(id: string, callback: (result: JsonContainer, company: Company) => void) {
        this.executeDelete('deleteCertified/' + id, callback)
    }
    deleteProc(id: string, type:String, callback: (result: JsonContainer, company: Company) => void) {
        this.executeDelete(`deleteProc/${type}/${id}`, callback)
    }

    listActiveCustomers(callback: (result: JsonContainer, roles: Company[]) => void) {
        this.executeGet('listActiveCustomers', callback)
    }

    listActiveMEICustomers(callback: (result: JsonContainer, roles: Company[]) => void) {
        this.executeGet('listActiveMEICustomers', callback)
    }

    loadLc116ByCnae(cnae: string, callback: (result: JsonContainer, roles: Lc116[]) => void) {
        this.executeGet('loadLc116ByCnae/' + cnae, callback)
    }

    loadTaxCodeByLc116(lc116: string, callback: (result: JsonContainer, roles: TaxCode[]) => void) {
        this.executeGet('loadTaxCodeByLc116/' + lc116, callback)
    }

    searchCompanyInSerpro(document: string, callback: (result: JsonContainer, company: Company) => void) {
        this.executeGet('searchCompanyInSerpro/' + document, callback)
    }

    getCnaeByCompanyId(companyId: string, callback: (result: JsonContainer, cnae: Cnae) => void) {
        this.executeGet('cnaeDesc/' + companyId, callback)
    }

    updateCompanyCNAE(companyId: string, cnae: Cnae, callback: (result: JsonContainer) => void) {
        this.executePut(`updateCompanyCNAE/${companyId}`, cnae, callback);
    }

    getAllCnae(callback: (result: JsonContainer, cnae: Cnae[]) => void) {
        this.executeGet('getAllCnae', callback)
    }

    deleteAttachment(id: string, destination: string, companyId: String, partnerIndex: number | -1, callback: (result: JsonContainer, company: Company) => void) {
        this.executeDelete(`deleteAttachment/${id}/${destination}/${companyId}/${partnerIndex}`, callback);
    }

    saveParticularity(particularity: Particularity, callback: (result: JsonContainer) => void) {
        this.executePost('saveParticularity', particularity, callback)
    }

    listParticularities(companyId: String, callback: (result: JsonContainer, particularities: Particularity[]) => void) {
        this.executeGet(`listParticularities/${companyId}`, callback)
    }


    loadCompany(companyId: string, callback: (result: JsonContainer, company: Company) => void) {
        this.executeGet('loadCompany/' + companyId, callback)
    }

    loadOnboardingProcess(callback: (result: JsonContainer, company: Company) => void) {
        this.executeGet('loadOnboarding', callback)
    }

    loadTotalAmout(callback: (result: JsonContainer, value: number) => void) {
        this.executeGet('totalAmountExt', callback)
    }

    loadTotalYearAll(callback: (result: JsonContainer, value: number) => void) {
        this.executeGet('totalAmountYearAll', callback)
    }


    loadTotalAmoutMonth(callback: (result: JsonContainer, value: { [key: string]: number }) => void): void {
        this.executeGet('totalAmountYear', (result, response) => {
            const monthlyTotals: { [key: string]: number } = response;
            callback(result, monthlyTotals);
        });
    }

    getBlockedCompaniesUpdatedThisMonth(callback: (result: JsonContainer, company: Company) => void) {
        this.executeGet('getBlockedCompaniesUpdatedThisMonth', callback)
    }

    loadCompanyCountByParentSocialName(parentCode: string, callback: (result: JsonContainer, data: { [key: string]: number }) => void): void {
        const encodedParentCode = encodeURIComponent(parentCode);
        const url = `companyCountByParentSocialName?parentCode=${encodedParentCode}`;
        this.executeGet(url, (result, response) => {
            const companyCounts: { [key: string]: number } = response;
            callback(result, companyCounts);
        });
    }

    getPlanLinkByCompanyId(companyId: string, callback: (result: JsonContainer, link: PlanLinkResponse) => void) {
        this.executeGet(`planLink/${companyId}`, callback);
    }

    loadTakersByCompanyId(companyId: string, callback: (result: JsonContainer, takers: any[]) => void) {
        this.executeGet(`loadTakersByCompanyId/${companyId}`, callback);
    }
}
